import { FC, useMemo } from 'react'
import { AbstractionToken } from 'services/biconomy/accountAbstraction.types'
import styled, { css } from 'styled-components'

import useAbstractionInfo from '../hooks/useAbstractionInfo'

type Props = {
	value: string
	valueToken: string
	onChange: (value: string) => void
}

const FundTemplates: FC<Props> = ({ value, valueToken, onChange }) => {
	const { estimateTradeCost } = useAbstractionInfo()

	const templates = useMemo(
		() =>
			[15, 30, 50, 100].map((v) => ({
				value: (v * parseFloat(estimateTradeCost.value)).toFixed(
					estimateTradeCost.asset === AbstractionToken.USDC ? 2 : 5
				),
				label: `${v} Txs`,
			})),
		[estimateTradeCost]
	)

	return (
		<FundSection>
			{templates.map((template) => (
				<FundTemplate
					key={template.value}
					active={template.value === value}
					onClick={() => onChange(template.value)}
				>
					<FundTemplateLabel active={template.value === value}>{template.label}</FundTemplateLabel>
					<FundTemplateValue active={template.value === value}>
						{template.value} {valueToken}
					</FundTemplateValue>
				</FundTemplate>
			))}
		</FundSection>
	)
}

const FundSection = styled.section`
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	overflow-x: auto;
`

const FundTemplate = styled.button<{ active: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border-radius: 4px;
	width: calc(25% - 3.5px);
	padding: 10px;
	line-height: 1;
	cursor: pointer;
	transition: all 0.2s ease-in-out;

	${(props) => css`
		border: ${props.theme.colors.selectedTheme.newTheme.button.default.border};
		border-color: ${props.active
			? props.theme.colors.selectedTheme.newTheme.button.fund.activeBorder
			: null};
		background-color: ${props.active
			? `${props.theme.colors.selectedTheme.newTheme.button.fund.activeBackground}`
			: 'transparent'};
	`}

	:hover {
		${(props) => css`
			border-color: ${props.theme.colors.selectedTheme.newTheme.text.primary};
		`}
	}
`

const FundTemplateLabel = styled.span<{ active: boolean }>`
	font-size: 13px;

	${(props) => css`
		color: ${props.active
			? props.theme.colors.selectedTheme.newTheme.button.fund.activeLabelColor
			: props.theme.colors.selectedTheme.newTheme.button.fund.labelColor};
		font-family: ${props.active ? props.theme.fonts.bold : props.theme.fonts.regular};
	`}
`

const FundTemplateValue = styled.span<{ active: boolean }>`
	font-size: 12px;

	color: ${(props) =>
		props.active
			? props.theme.colors.selectedTheme.newTheme.button.fund.activeValueColor
			: props.theme.colors.selectedTheme.newTheme.button.fund.valueColor};
`

export default FundTemplates
